import React, { useId, useState } from 'react';
import { X } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  DatePicker,
  Label,
  RadioButtonGroup,
  Separator,
  Textarea,
} from '@roger/ui-components';
import type { GoalTemplateDetail } from '../../services/api/types.gen';
import { addGoalToVisit } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import { renderEditableInstructionFields } from '../../utils/goals';
import CreateInterventionFromTemplateCard from './CreateInterventionFromTemplateCard';

interface CreateGoalFromTemplateCardProps {
  template: GoalTemplateDetail | null;
  episodeStartDate: string;
  episodeEndDate: string;
  visitId: string;
  onSave?: () => void;
  onCancel?: () => void;
}

const GOAL_TYPE_OPTIONS = [
  { value: 'short_term', label: 'Short term goal' },
  { value: 'long_term', label: 'Long term goal' },
];

const CreateGoalFromTemplateCard: React.FC<CreateGoalFromTemplateCardProps> = ({
  template,
  episodeStartDate,
  episodeEndDate,
  visitId,
  onSave,
  onCancel,
}) => {
  const [targetDate, setTargetDate] = useState<Date | undefined>(undefined);
  const [isEndOfEpisode, setIsEndOfEpisode] = useState(false);
  const [goalType, setGoalType] = useState<'short_term' | 'long_term'>(
    'short_term'
  );
  const [instructionValues, setInstructionValues] = useState<
    Record<string, string>
  >({});
  const [freeTextInstruction, setFreeTextInstruction] = useState('');
  const [interventionData, setInterventionData] = useState<
    Record<string, { instruction: string; isValid: boolean }>
  >({});
  const [removedInterventionIds, setRemovedInterventionIds] = useState<
    Set<string>
  >(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const uid = useId();
  const { success: showSuccess, error: showError } = useToast();

  const minDate = parseISOToLocalDate(episodeStartDate);
  const maxDate = parseISOToLocalDate(episodeEndDate);

  const isFreeText = template === null;
  const editableKeys = isFreeText
    ? []
    : Object.entries(template.instruction)
        .filter(([, field]) => field.editable)
        .map(([key]) => key);

  const visibleAttachedInterventions = isFreeText
    ? []
    : template.attached_interventions.filter(
        iv => !removedInterventionIds.has(iv.id)
      );
  const isFormValid = isFreeText
    ? !!targetDate && !!freeTextInstruction.trim()
    : !!targetDate &&
      editableKeys.every(key => instructionValues[key]?.trim()) &&
      visibleAttachedInterventions.every(
        iv => interventionData[iv.id]?.isValid
      );

  const assembleInstruction = () => {
    if (isFreeText) {
      return freeTextInstruction.trim();
    }
    const entries = Object.entries(template.instruction).sort(
      ([a], [b]) => Number(a) - Number(b)
    );
    return entries
      .map(([key, field]) =>
        field.editable ? (instructionValues[key] ?? '') : field.text
      )
      .join(' ');
  };

  const handleDateChange = (date: Date | undefined) => {
    setIsEndOfEpisode(false);
    if (!date) {
      setTargetDate(undefined);
      return;
    }
    const time = date.getTime();
    if (minDate && time < minDate.getTime()) {
      setTargetDate(minDate);
    } else if (maxDate && time > maxDate.getTime()) {
      setTargetDate(maxDate);
    } else {
      setTargetDate(date);
    }
  };

  const handleInstructionChange = (key: string, value: string) => {
    setInstructionValues(prev => ({ ...prev, [key]: value }));
  };

  const handleFreeTextInstructionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFreeTextInstruction(e.target.value);
  };

  const handleEndOfEpisodeChange = (checked: boolean | 'indeterminate') => {
    const checkedVal = checked === true;
    setIsEndOfEpisode(checkedVal);
    if (checkedVal) {
      const end = parseISOToLocalDate(episodeEndDate);
      if (end) setTargetDate(end);
    }
  };

  const handleGoalTypeChange = (val: string) => {
    setGoalType(val as 'short_term' | 'long_term');
  };

  const handleInterventionChange =
    (ivId: string) => (data: { instruction: string; isValid: boolean }) => {
      setInterventionData(prev => ({ ...prev, [ivId]: data }));
    };

  const handleRemoveIntervention = (ivId: string) => () => {
    setRemovedInterventionIds(prev => new Set([...prev, ivId]));
  };

  const handleSave = async () => {
    if (!targetDate) return;
    setIsSaving(true);
    try {
      await addGoalToVisit({
        path: { visit_id: visitId },
        body: {
          goal_template_id: isFreeText ? null : template.id,
          target_date: formatDateToISO(targetDate),
          is_for_end_of_episode: isEndOfEpisode,
          length: goalType,
          name: isFreeText ? 'Free Text' : template.name,
          instruction: assembleInstruction(),
          interventions: visibleAttachedInterventions.map(iv => ({
            intervention_id: iv.id,
            name: iv.name,
            instruction: interventionData[iv.id]?.instruction ?? '',
          })),
        },
        throwOnError: true,
      });
      showSuccess('Goal added', 'The goal has been added to the visit.');
      onSave?.();
    } catch {
      showError('Error adding goal', 'There was an error adding the goal.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className='self-start p-2.5 sm:p-2.5 md:p-2.5'>
      <CardHeader className='mb-2 relative pr-8 p-0'>
        <CardTitle className='text-sm font-semibold leading-tight'>
          <span className='text-muted-foreground font-normal'>New Goal: </span>
          {isFreeText ? 'Free Text' : template.name}
        </CardTitle>
        {onCancel && (
          <button
            type='button'
            onClick={onCancel}
            className='absolute top-0 right-0 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
            aria-label='Discard and remove'
          >
            <X className='h-4 w-4' />
          </button>
        )}
      </CardHeader>

      <CardContent className='space-y-3 p-0'>
        <div className='space-y-2'>
          <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
            Target date for meeting a goal
          </p>
          <div className='flex flex-wrap items-center gap-2'>
            <DatePicker
              date={targetDate}
              onDateChange={handleDateChange}
              datePickerSize='sm'
              typable
              showClearButton
              minDate={minDate}
              maxDate={maxDate}
              className='w-44'
            />
            <div className='flex items-center gap-2'>
              <Checkbox
                id={`end-of-episode-${uid}`}
                checked={isEndOfEpisode}
                onCheckedChange={handleEndOfEpisodeChange}
                checkboxSize='sm'
              />
              <Label
                htmlFor={`end-of-episode-${uid}`}
                className='text-sm cursor-pointer select-none'
              >
                For end of episode
              </Label>
            </div>
            <div className='flex shrink-0'>
              <RadioButtonGroup
                value={goalType}
                onValueChange={handleGoalTypeChange}
                options={GOAL_TYPE_OPTIONS}
                orientation='horizontal'
              />
            </div>
          </div>
        </div>

        <Separator />

        <div className='space-y-2'>
          <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
            Instruction
          </p>
          <div className='leading-relaxed'>
            {isFreeText ? (
              <Textarea
                placeholder='Enter goal instruction...'
                value={freeTextInstruction}
                onChange={handleFreeTextInstructionChange}
                textareaSize='default'
                className='min-h-[64px]'
              />
            ) : (
              renderEditableInstructionFields(
                template.instruction,
                instructionValues,
                handleInstructionChange
              )
            )}
          </div>
        </div>

        {!isFreeText && template.attached_interventions.length > 0 && (
          <>
            <Separator />
            <div className='space-y-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Interventions
              </p>
              {visibleAttachedInterventions.map(iv => (
                <CreateInterventionFromTemplateCard
                  key={iv.id}
                  template={iv}
                  onChange={handleInterventionChange(iv.id)}
                  onCancel={handleRemoveIntervention(iv.id)}
                />
              ))}
            </div>
          </>
        )}

        <div className='flex justify-end pt-1'>
          <Button
            size='sm'
            disabled={!isFormValid || isSaving}
            onClick={handleSave}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CreateGoalFromTemplateCard;
